import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

interface ILocationObserver {
  children: ReactNode;
}

const LocationObserver = ({ children }: ILocationObserver) => {
  const { hasIntersected, ref } = useObserver<HTMLDivElement>();

  return <div ref={ref}>{hasIntersected && children}</div>;
};

const useObserver = <T extends Element>() => {
  const options = {
    root: null,
    rootMargin: '0px',
    threshold: 0,
  };
  const ref = useRef<T>(null);
  const [hasIntersected, setHasIntersected] = useState(false);
  const load = useCallback((entires: Array<IntersectionObserverEntry>) => {
    const entry = entires.find((entry) => entry.target === ref.current);

    if (entry && entry.isIntersecting) {
      setHasIntersected(true);
      ref.current && observer.current.unobserve(ref.current);
    }
  }, []);
  const observer = useRef(new IntersectionObserver(load, options));

  useEffect(() => {
    if (!ref.current) return;

    const el = ref.current;
    const observerCurrent = observer.current;
    observerCurrent.observe(el);
    return () => {
      observerCurrent.unobserve(el);
    };
  }, []);

  return { hasIntersected, ref };
};

export default LocationObserver;
